/**
 * Native app environment detection — Contract Group® Quantis Platform
 *
 * Checks whether the web app is currently running inside a native iOS/macOS
 * WKWebView shell rather than a regular browser tab.  Three independent
 * signals are tested so detection is reliable even if the native shell only
 * exposes a subset of the bridge:
 *
 *  1. window.webkit.messageHandlers  — any registered WKScriptMessageHandler
 *  2. window.__QUANTIS_DEVICE_TOKEN__ — APNs token injected by the native shell
 *  3. ?source=native URL param        — set by the native deep-link login flow
 *  4. sessionStorage flag             — written once when (3) is first seen so
 *                                       it survives in-app navigation that
 *                                       strips the query string
 */

const SESSION_KEY = 'cg_native_app';

/**
 * Returns true when the page is running inside the Quantis native shell.
 * Safe to call at module initialisation time (no React dependency).
 */
export function isNativeApp(): boolean {
  if (typeof window === 'undefined') return false;

  // 1. WKWebView bridge — any message handler present means native shell
  if ((window as any).webkit?.messageHandlers) return true;

  // 2. APNs token injected by native layer before page load
  if ((window as any).__QUANTIS_DEVICE_TOKEN__) return true;

  // 3 + 4. ?source=native query param (login handoff) — persist to sessionStorage
  //         so subsequent navigation without the param still detects native mode.
  try {
    if (sessionStorage.getItem(SESSION_KEY) === 'true') return true;

    const params = new URLSearchParams(window.location.search);
    if (params.get('source') === 'native') {
      sessionStorage.setItem(SESSION_KEY, 'true');
      return true;
    }
  } catch {
    // sessionStorage can be blocked in some contexts — treat as non-native
  }

  return false;
}

/**
 * Returns true when the page runs inside ANY native shell — the iOS/macOS
 * WKWebView shells (isNativeApp) OR the Windows/macOS Tauri desktop shell.
 *
 * Use this (not isNativeApp) to gate web-only surfaces that make no sense in a
 * first-party app: the cookie-consent banner, the web-push permission nudge,
 * browser install prompts. Do NOT use it to trigger WKWebView-specific
 * behaviour (NativeBridge, safe-area insets) — Tauri has no WKWebView bridge,
 * so those must stay on isNativeApp().
 */
export function isNativeShell(): boolean {
  if (isNativeApp()) return true;
  if (typeof window === 'undefined') return false;
  // Android installs no webkit.messageHandlers polyfill (ADR-017 in the
  // Quantis-Android team docs) so isNativeApp() stays false there by design —
  // this is the one detection branch that recognises the shell anyway.
  if (window.__QUANTIS_NATIVE__ === true) return true;
  // Tauri (desktop) exposes these globals; matches isTauriRuntime() without
  // importing the Tauri shell module into this dependency-free util.
  return '__TAURI_INTERNALS__' in window || '__TAURI__' in window;
}

/**
 * True only inside the iOS/iPadOS WKWebView shell, where the native CallKit
 * stack owns calling and the web must not ring as well.
 *
 * Reads the explicit attribute the iOS bridge stamps at documentStart, never a
 * user-agent sniff: the macOS shell is also isNativeApp() and also reports
 * navigator.platform === 'MacIntel', and its CallManager is a no-op stub, so a
 * false positive there would delete calling on macOS with no error and no
 * fallback. An absent attribute means "keep the web path live" — a duplicate
 * ring is a nuisance, a dead platform is an outage.
 */
export function isIOSNativeShell(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-quantis-native-platform') === 'ios';
}
